import React, { useCallback, useState } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Hidden, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import DeleteModal from '@acnb/CustomComponents/_Modals/DeleteModal';
import {
  useActiveMerchandisingProgramQuote,
  useDeleteMerchandisingProgramQuoteItem,
  useMerchandisingProgramQuoteSortedSeason,
} from 'services/Data/MerchandisingProgramQuoteService';
import { getMerchandisingProgramItemSubLabel } from 'services/Utilities/Utilities';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import { ApplicationIcons } from 'icons/ApplicationIcons';
import useMobile from 'services/Hooks/useMobile';
import MerchandisingProgramQuoteItemCard from '../MerchandisingProgramQuote/MerchandisingProgramQuoteItemCard';

const useStyles = makeStyles(theme => ({
  mainButton: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 0 1rem rgba(255, 222, 0, 0.9)',
    },
  },
  guideLink: {
    color: 'rgba(51, 51, 51, 0.8)',
    textDecoration: 'underline',
    '&:hover': {
      color: 'rgba(27, 27, 27, 0.8)',
      textDecoration: 'underline',
    },
  },
}));

const CompleteMerchandisingProgram = () => {
  const classes = useStyles();

  let hist = useHistory();
  const { labelReference } = useLanguageReference();
  const isMobile = useMobile();

  const { t } = useTranslation();
  const accountReducer = useSelector(state => state.AccountReducer);

  const { data: sortedMerchandisingProgramSeasonEnums } = useMerchandisingProgramQuoteSortedSeason();

  const { data: merchandisingProgramQuote } = useActiveMerchandisingProgramQuote(accountReducer.isLoggedIn);
  const { mutate: deleteMerchandisingProgramQuoteItem } = useDeleteMerchandisingProgramQuoteItem();

  const [itemToDelete, setItemToDelete] = useState();

  const handleOpenDeleteItemModal = useCallback(
    item => {
      setItemToDelete(item);
    },
    [setItemToDelete],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setItemToDelete(null);
  }, [setItemToDelete]);

  return (
    <Box>
      <DeleteModal
        handleCloseModal={handleCloseDeleteModal}
        item={itemToDelete}
        message={t('deleteItemFromMerchandisingProgramQuoteText', {
          detail:
            itemToDelete &&
            getMerchandisingProgramItemSubLabel(itemToDelete, labelReference, sortedMerchandisingProgramSeasonEnums),
          name: itemToDelete?.name,
        })}
        deleteAction={deleteMerchandisingProgramQuoteItem}
      />

      <CmtCard sx={{ marginTop: 5 }}>
        <CmtCardHeader
          title={t('merchandisingProgramQuote')}
          icon={<ApplicationIcons.MerchandisingProgram />}></CmtCardHeader>
        <CmtCardContent>
          {merchandisingProgramQuote?.quoteItems?.map(qi => (
            <MerchandisingProgramQuoteItemCard key={qi.id} item={qi} handleOpenDeleteItemModal={handleOpenDeleteItemModal} />
          ))}
          <GridContainer className={isMobile ? '' : classes.cartFooter}>
            <Hidden mdDown implementation="js">
              <GridItem xs={12}>
                <Divider />
              </GridItem>
            </Hidden>
            <GridItem xs={12} md={7} lg={9}></GridItem>
            <GridItem xs={12} md={5} lg={3}>
              <Button
                variant="contained"
                color="secondary"
                sx={{
                  borderRadius: 0,
                  color: '#000',
                  mt: 6,
                  width: '100%',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#EFBA29',
                    boxShadow: 'none',
                  },
                }}
                disabled={(merchandisingProgramQuote?.quoteItems?.length || 0) <= 0}
                onClick={() => {
                  hist.push('/dealer/home-page/merchandising-program/check-out');
                }}>
                <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 }, py: 2 }}>
                  {t('proceedCheckoutMerchandisingProgramQuote')}
                </Typography>
              </Button>
            </GridItem>
          </GridContainer>
        </CmtCardContent>
      </CmtCard>
    </Box>
  );
};

export default CompleteMerchandisingProgram;
